import { useEffect, useState } from 'react'

export function usePopupSeenTracker({
  popupKey,
  seenPopups,
  updateUserSettings,
  persistLocally = true,
  storagePrefix = 'popup',
}: {
  popupKey: string
  seenPopups?: Record<string, boolean>
  updateUserSettings: (settings: any) => Promise<unknown> | unknown
  persistLocally?: boolean
  storagePrefix?: string
}) {
  const storageKey = `${storagePrefix}:${popupKey}`

  // null = loading, true/false = real state
  const [hasSeen, setHasSeen] = useState<boolean | null>(null)

  useEffect(() => {
    // don't run until seenPopups is loaded or explicitly undefined
    if (seenPopups === undefined) return

    const seenInBackend = seenPopups?.[popupKey]
    const seenInLocal = localStorage.getItem(storageKey) === 'true'

    if (seenInBackend || seenInLocal) {
      setHasSeen(true)
    } else {
      setHasSeen(false)
    }
  }, [seenPopups, popupKey, storageKey])

  const markAsSeen = async () => {
    // Optimistic UI update — local state and storage flip immediately.
    setHasSeen(true)
    if (persistLocally) {
      localStorage.setItem(storageKey, 'true')
    }

    // Always sync to backend if backend doesn't already have the key. This
    // covers the case where a previous session set localStorage but the
    // backend write silently failed (offline, dropped request, etc.) — so
    // clearing storage on a different device doesn't reset the seen state.
    const alreadyOnBackend = !!seenPopups?.[popupKey]
    if (alreadyOnBackend) return

    try {
      await updateUserSettings({
        seenPopups: { ...(seenPopups || {}), [popupKey]: true },
      })
    } catch (err) {
      console.error(`Failed to persist popup-seen for "${popupKey}":`, err)
      // Local state stays true — the user has visually seen the popup. The
      // hook will retry the backend write the next time markAsSeen fires
      // (since backend still doesn't have the key).
    }
  }

  return { hasSeen, markAsSeen }
}
